/**
 * GovRAMP program page — V103-3-6 (design pass `v103-programs-ui` round 4,
 * `a-govramp.html`). The Prepare and Operate tabs of the program shell:
 *
 *   header      impact level · held tier (or "pursuing") · lead, and the
 *               non-interactive tier ladder; "Start pursuing <next>" opens the
 *               tier change (waits while ConMon is late or a lapse stands)
 *   status      the same status line, pips, date and ONE primary as Today
 *               (nearest hard date wins) — or the Snapshot score card
 *   ConMon      the open cycle's checklist (mark done, the item's own page,
 *               Submit at N of N); late → red card with "Record a lapse notice"
 *               (a recorded GovRAMP notice; never a computed date)
 *   readiness   controls met for the tier being assessed, meter + legend;
 *               Record Ready booking while pursuing Ready (ONE record that the
 *               FedRAMP Class A page reads too)
 *   controls    Not met → Fix, No evidence → Collect (never merged); shared
 *               evidence carries "Also counts for FedRAMP 20x"
 *
 * The Audit tab (GovrampAudit) records GovRAMP's decision (tier granted) and
 * the Ready booking.
 *
 * Reads (any member): GET /programs/:id/govramp, /govramp/tier-delta, the
 * FedRAMP objective readiness (shared-evidence tags). Writes (owner / admin or
 * the program lead; server-enforced, hidden otherwise): program events, ConMon
 * submit / generate, tier change, evidence upload (also editors).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Field, Input, Select, Spinner } from '@aigovops/design-system';
import type { ProgramCard } from '../../lib/api';
import { getObjectiveReadiness, listWorkspaceMembers } from '../../lib/api';
import { getProgramManifest, sourceNameOf } from '@aigovops/core';
import { useOptionalAuth } from '../../lib/auth';
import { invalidationMap, queryKeys } from '../../lib/query-keys';
import { usePrograms } from '../../lib/workspace-navigation';
import {
  changeGovrampTier, endProgramEvent, generateConmonCycles, getGovrampOverview, getGovrampTierDelta, recordProgramEvent, submitConmonCycle,
  type GovrampOverview,
} from '../../lib/govramp-api';
import {
  cycleModel, daysText, fmtShort, govrampStatus, headerMeta, nextTier, pct, readinessModel, snapshotLevers, snapshotMovers, tierLabel, tierLadder,
  type CycleModel, type GovrampControl, type GovrampStatus,
} from '../../lib/govramp-model';
import { JumpLink } from '../today/shared';
import { AskAdmin } from '../AskAdmin';
import { coveringAcceptance, objectiveItems } from '../../lib/couldnt-check';
import { AcceptedLine, AcceptRisk, CouldntCheckChip, CouldntCheckCount, CouldntCheckFixes, CouldntCheckWhy, ShowAllReasons } from '../CouldntCheck';
import { UploadForObjective } from './CmmcAssess';
import { BookingForm, Datebox, ExtLink, FIND_3PAO_URL, Glyph, LapseNoticeForm, Pips, useToast } from './govramp-parts';
import { rollupHeadline, useRollupContext, useRollupData, type RollupContextValue } from '../sweep/ProgramRollup';
import { currentStage } from '../../lib/sweep-model';
import '../../pages/govramp.css';

export interface GovrampPanelProps {
  card: ProgramCard;
  slug: string;
  prefix: string;
  canManage: boolean;
  role: string;
  names: ReadonlyMap<string, string>;
  /** Shown when the program has no period yet (the shell's "open the first window"). */
  fallback: ReactNode;
}

const dayOf = (o: Pick<GovrampOverview, 'generated_at'>) => o.generated_at.slice(0, 10);

export function useGovrampOverview(slug: string, program: string, enabled = true) {
  return useQuery({ queryKey: queryKeys.govrampOverview(slug, program), queryFn: () => getGovrampOverview(slug, program), enabled, retry: false, staleTime: 30_000 });
}

/** FedRAMP's tests (program-neutral evidence): a GovRAMP control sharing one "also counts" there. */
function useFedrampTestKeys(slug: string): ReadonlySet<string> {
  const programs = usePrograms(slug);
  const fr = programs.data?.programs.find((c) => c.program === 'fedramp-cr26') ?? null;
  const period = fr?.periods.find((p) => p.active && p.period_id)?.period_id ?? null;
  const r = useQuery({
    queryKey: queryKeys.objectiveReadiness(slug, 'fedramp-cr26', period ?? ''), queryFn: () => getObjectiveReadiness(slug, 'fedramp-cr26', period!),
    enabled: !!period, retry: false, staleTime: 5 * 60_000,
  });
  return useMemo(() => new Set((r.data?.readiness?.requirements ?? []).flatMap((q) => q.objectives.flatMap((o) => o.test_keys))), [r.data]);
}

// ---- the status card (program page AND Today) ----------------------------------------------------

/**
 * One status card for GovRAMP's tier world: status line + glyph, pips,
 * consequence, the controls subline (Today), the ONE date and the ONE
 * primary. Today and the program page render this same component.
 */
export function GovrampStatusCard({ o, s, variant, title, primary, secondary, children, rollup }: {
  o: GovrampOverview; s: GovrampStatus; variant: 'page' | 'today'; title?: ReactNode;
  primary: ReactNode; secondary?: ReactNode; children?: ReactNode;
  /** BU-3: once a sweep exists, the status line is the roll-up's ("Core · 26 of 44 controls met", Off track); ConMon stays the secondary line. */
  rollup?: { text: string; status: { tone: 'on_track' | 'at_risk' | 'off_track'; text: string } } | null;
}) {
  const todayDay = dayOf(o);
  const r = readinessModel(o);
  const tone = rollup ? (rollup.status.tone === 'off_track' ? 'bad' : rollup.status.tone === 'at_risk' ? 'warn' : 'ok') : s.tone;
  const glyph = tone === 'bad' ? 'not' : tone === 'warn' ? 'act' : 'done';
  const headingId = `gr-status-${variant}`;
  return (
    <section className="gr-status" data-tone={tone} aria-labelledby={headingId} data-part={`govramp-status-${variant}`}>
      {title ? <h2 id={headingId} className="gr-prog-name">{title}</h2> : <h2 id={headingId} className="gp-sr-only">GovRAMP status</h2>}
      <div className="gr-status-grid">
        <div className="gr-stack">
          {rollup ? (
            <p className="gr-status-line" data-part="status-line"><Glyph status={glyph} />{rollup.text}{' '}
              <span className="sw-status" data-tone={rollup.status.tone} data-part="rollup-status">{rollup.status.text}</span></p>
          ) : <p className="gr-status-line" data-part="status-line"><Glyph status={glyph} />{variant === 'today' ? s.todayHeadline : s.headline}</p>}
          {s.cycle ? <Pips done={s.cycle.done} total={s.cycle.total} tone={s.tone} label={s.cycle.label} /> : null}
          {s.consequence ? <p className="gr-consequence" data-part="consequence">{s.consequence}{variant === 'page' && o.lapse ? <span className="gr-small gr-dim">{` · recorded`}</span> : null}</p> : null}
          {variant === 'today' && r && !rollup ? (
            <p className="gr-subline" data-part="controls">{s.pursuing && s.held ? `${r.met} of ${r.total} for ${tierLabel(s.pursuing)}` : `${r.met} of ${r.total} controls`}</p>
          ) : null}
          {children}
        </div>
        {s.date ? <Datebox label={s.date.label} date={fmtShort(s.date.date, todayDay)} note={s.date.note} late={s.date.late} /> : null}
      </div>
      {primary || secondary ? <div className="gr-actions">{primary}{secondary}</div> : null}
    </section>
  );
}

// ---- the page --------------------------------------------------------------------------------

export function GovrampProgram({ card, slug, prefix, canManage, role, names, fallback }: GovrampPanelProps) {
  const program = card.profile_id;
  const q = useGovrampOverview(slug, program);
  const [toastNode, toast] = useToast();
  const [sp, setSp] = useSearchParams();
  const tierTo = sp.get('tier');
  const fedrampTests = useFedrampTestKeys(slug);
  const hasFedramp = (usePrograms(slug).data?.programs ?? []).some((c) => c.program === 'fedramp-cr26');
  const rollup = useRollupContext();
  const rollupShown = !!rollup;
  // BU-3: the status card says what the roll-up says on every view (the same cached reads as the shell).
  const gm = getProgramManifest(card.program);
  const roll = useRollupData(slug, gm, card);
  const rollHead = roll.data && gm ? rollupHeadline(gm, roll.data) : null;

  if (q.isLoading) return <Spinner label="Loading GovRAMP" />;
  if (q.error || !q.data) {
    return <Alert variant="error"><p>GovRAMP could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  }
  const o = q.data;
  if (!o.period) return <>{fallback}</>;
  const todayDay = dayOf(o);
  const s = govrampStatus(o, todayDay);
  const lead = o.lead_user_id ? names.get(o.lead_user_id) ?? null : null;
  const next = nextTier(o.tier);
  const canPursue = s.world === 'tier' && !s.pursuing && !!next && !!s.held;
  const openTier = (to: string | null) => { const n = new URLSearchParams(sp); if (to) n.set('tier', to); else n.delete('tier'); setSp(n, { replace: true }); };
  const r = readinessModel(o);
  const cycles = o.cycles.map((c) => cycleModel(c, todayDay));
  const lateCycles = cycles.filter((c) => c.late);
  const openCycles = cycles.filter((c) => !c.late);
  const pursuingReady = s.world === 'tier' && !!s.pursuing && ['ready', 'authorized', 'provisional'].includes(s.pursuing);
  const bookingFirst = pursuingReady && !o.ready_booking;

  // V109-5-5: with the roll-up heading the page, it carries readiness and the one filled primary.
  const statusPrimary = (label: string, filled: boolean) => (
    <JumpLink to="gr-conmon-h" className={`gp-linkbtn${filled && !rollupShown ? ' btn-primary' : ''}`}>{label}</JumpLink>
  );

  return (
    <div className="gr-page" data-part="govramp-program">
      {toastNode}
      <div className="gr-head">
        <div>
          <p className="gr-meta" data-part="govramp-meta">{headerMeta(o, s, lead, todayDay)}</p>
          <ol className="gr-ladder" aria-label="GovRAMP tier progress" data-part="tier-ladder">
            {tierLadder(o, todayDay).map((rung) => (
              <li key={rung.tier} data-state={rung.state}>
                <span className="gr-lnode" aria-hidden="true">{rung.state === 'done' ? '✓' : rung.state === 'lapsed' ? '✕' : ''}</span>
                {rung.label}{rung.note ? <em>{` · ${rung.note}`}</em> : null}
                {rung.state === 'done' ? <span className="gp-sr-only"> completed</span> : null}
              </li>
            ))}
          </ol>
        </div>
        {canPursue && canManage && !tierTo ? (
          s.tierChangeBlocked
            ? <p className="gr-small gr-dim" data-part="pursue-blocked">{`Start pursuing ${tierLabel(next)} · ${s.lapsed ? `After ${tierLabel(s.held)} is restored` : `After ${s.cycle?.label ?? 'ConMon'} is submitted`}`}</p>
            : <Button variant="ghost" onClick={() => openTier(next)} data-part="pursue-open">{`Start pursuing ${tierLabel(next)}`}</Button>
        ) : null}
      </div>

      {s.world === 'snapshot'
        ? <SnapshotCard o={o} s={s} slug={slug} canManage={canManage} onToast={toast} />
        : rollup && !s.late && !s.lapsed
          // Built-UI #2: under the roll-up the period's status collapses to one line (the ConMon card below is the action).
          ? <StageLead o={o} s={s} rollup={rollup} prefix={prefix} />
          : <GovrampStatusCard o={o} s={s} variant="page" rollup={rollHead}
              primary={s.primary && !s.lapsed && !s.late ? statusPrimary(s.primary.label, !bookingFirst) : null} />}

      {/* Late / lapsed: the ConMon card is the action. */}
      {lateCycles.map((c) => (
        <ConmonCard key={c.id} c={c} o={o} s={s} slug={slug} program={program} canManage={canManage} late onToast={toast} />
      ))}

      {tierTo && canManage ? <TierChange o={o} s={s} slug={slug} program={program} to={tierTo} hasFedramp={hasFedramp} onClose={(msg) => { openTier(null); if (msg) toast({ text: msg, focusId: 'gr-status-page' }); }} /> : null}

      {s.world === 'snapshot' ? <Movers o={o} slug={slug} prefix={prefix} role={role} canManage={canManage} /> : null}

      {s.world === 'tier' && r && (!rollupShown || pursuingReady) ? (
        <ReadinessHero o={o} r={r} slug={slug} canManage={canManage} pursuingReady={pursuingReady} onToast={toast} slim={rollupShown} />
      ) : o.readiness_error ? <Alert variant="error"><p>{`Readiness could not load. ${o.readiness_error}`}</p></Alert> : null}

      {openCycles.length ? openCycles.slice(0, 1).map((c) => (
        <ConmonCard key={c.id} c={c} o={o} s={s} slug={slug} program={program} canManage={canManage} late={false} onToast={toast} />
      )) : !lateCycles.length ? <NoCycles o={o} slug={slug} program={program} canManage={canManage} /> : null}

      {/* Built-UI #2: under the roll-up the filtered control list (below the panel) replaces the open-controls grid. */}
      {s.world === 'tier' && r && !rollupShown ? <OpenControls r={r} slug={slug} prefix={prefix} program={program} periodId={o.period.period_id} role={role} canManage={canManage} fedrampTests={fedrampTests} /> : null}
    </div>
  );
}

/**
 * Built-UI #2 (approved one line): "Core period · October ConMon · 2 of 4 · due Oct 18 · Open Core · Operate".
 * The cycle's own stage: a jump to its card when it is on this page, else a link to that stage's card.
 */
function StageLead({ o, s, rollup, prefix }: { o: GovrampOverview; s: GovrampStatus; rollup: RollupContextValue; prefix: string }) {
  const c = rollup.data.conmon;
  const here = currentStage(rollup.card);
  const tier = tierLabel(s.held ?? o.tier);
  return (
    <ul className="sw-stage-lead" data-part="stage-lead">
      {c ? (
        <li>
          <b>{`${c.tier} period`}</b>
          <span aria-hidden="true">{' · '}</span>{c.label}
          <span aria-hidden="true">{' · '}</span>{`${c.done} of ${c.total}`}
          <span aria-hidden="true">{' · '}</span>{`due ${fmtShort(c.due, c.todayDay)}`}
          <span aria-hidden="true">{' · '}</span>
          {c.stage === here
            ? <JumpLink to="gr-conmon-h">{`Go to the ${c.label} checklist`}</JumpLink>
            : <Link to={`${prefix}/programs/${rollup.m.key}/${c.stage}#gr-conmon-h`}>{`Open ${c.tier} · ${c.stage === 'operate' ? 'Operate' : 'Prepare'}`}</Link>}
        </li>
      ) : <li><b>{`${tier} period`}</b><span aria-hidden="true">{' · '}</span>{s.headline}</li>}
    </ul>
  );
}

// ---- Snapshot ------------------------------------------------------------------------------------

function SnapshotCard({ o, s, slug, canManage, onToast }: { o: GovrampOverview; s: GovrampStatus; slug: string; canManage: boolean; onToast: ReturnType<typeof useToast>[1] }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const todayDay = dayOf(o);
  if (!o.snapshot) {
    return (
      <section className="gr-status" data-tone="warn" aria-labelledby="gr-snap-h" data-part="snapshot-card">
        <h2 id="gr-snap-h" className="gr-hero-label">Snapshot score</h2>
        <p>{o.snapshot_error ?? 'The Snapshot score is not available yet.'}</p>
        <Link className="gp-linkbtn" to={`/w/${slug}/app/queue`}>Open the review queue</Link>
      </section>
    );
  }
  const score = o.snapshot.score.percent;
  const last = o.snapshot.improvement.last_submitted_percent;
  const lastOn = o.last_submitted?.kind === 'snapshot' ? fmtShort(o.last_submitted.submitted_at, todayDay) : null;
  const flat = o.snapshot.improvement.warning;
  const lev = snapshotLevers(o);
  const projected = Math.min(100, score + lev.fixGain + lev.collectGain);
  const delta = last == null ? null : Math.round((score - last) * 100) / 100;
  const cycle = s.cycle;
  const submit = async () => {
    if (!cycle || busy) return;
    setBusy(true); setError(null);
    try {
      await submitConmonCycle(slug, o.profile_id, cycle.periodId, cycle.id, { snapshot_score_percent: score });
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
      setConfirm(false);
      onToast({ text: `Snapshot submitted · ${pct(score)}`, focusId: 'gr-snap-h' });
    } catch (e) { setError(e instanceof Error ? e.message : 'The Snapshot could not be recorded.'); }
    finally { setBusy(false); }
  };
  const tickLabel = last != null ? `Last ${pct(last)}${lastOn ? ` · ${lastOn}` : ''}` : null;
  return (
    <section className="gr-status" data-tone={s.tone} aria-labelledby="gr-snap-h" data-part="snapshot-card">
      <h2 id="gr-snap-h" className="gr-hero-label" tabIndex={-1}>Snapshot score</h2>
      <div className="gr-status-grid">
        <div className="gr-stack">
          <p className="gr-status-line"><Glyph status={flat ? 'act' : s.tone === 'bad' ? 'not' : 'done'} />{s.headline}</p>
          <p className="gr-snap-row">
            <span className="gr-score" data-part="score">{pct(score)}</span>
            {delta != null ? (
              <span className="gr-trend">
                <span className={delta > 0 ? 'gr-up' : delta < 0 ? 'gr-down' : 'gr-flat'}><span aria-hidden="true">{delta > 0 ? '▲' : delta < 0 ? '▼' : '→'}</span>
                  <span className="gp-sr-only">{delta > 0 ? 'up' : delta < 0 ? 'down' : 'unchanged'}</span>{` ${delta > 0 ? '+' : ''}${pct(delta)}`}</span>
                {lastOn ? ` since ${lastOn}` : ''}
              </span>
            ) : null}
          </p>
        </div>
        {s.date ? <Datebox label={s.date.label} date={fmtShort(s.date.date, todayDay)} note={s.date.note} late={s.date.late} /> : null}
      </div>
      <div>
        <div className="gr-scorebar" data-tone={flat ? 'warn' : 'ok'} role="img" data-part="scorebar"
          aria-label={`Score ${pct(score)} of 100%${last != null ? `; last submitted ${pct(last)}${lastOn ? ` on ${lastOn}` : ''}` : ''}${flat && projected > score ? `; ${pct(projected)} projected after ${lev.fix.length} fixes and ${lev.collect.length} collections` : ''}`}>
          <span className="gr-fill" style={{ width: `${score}%` }} />
          {flat && projected > score ? <span className="gr-proj" style={{ left: `${score}%`, width: `${projected - score}%` }} /> : null}
          {last != null ? <><span className="gr-tick" style={{ left: `${last}%` }} /><span className="gr-tick-l" style={{ left: `${last}%` }} aria-hidden="true">{tickLabel}</span></> : null}
        </div>
        <div className="gr-scale" aria-hidden="true"><span>0%</span>{flat && projected > score ? <span>{`Hatched · projected ${pct(projected)}`}</span> : null}<span>100%</span></div>
      </div>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div className="gr-actions">
        {flat ? <>
          {lev.fix.length ? <JumpLink to="gr-movers-h" className="gp-linkbtn btn-primary">{`Fix ${lev.fix.length} · +${pct(lev.fixGain)}`}</JumpLink> : null}
          {lev.collect.length ? <JumpLink to="gr-movers-h" className={`gp-linkbtn${lev.fix.length ? '' : ' btn-primary'}`}>{`Collect ${lev.collect.length} · +${pct(lev.collectGain)}`}</JumpLink> : null}
          {canManage && cycle ? <Button variant="ghost" aria-expanded={confirm} aria-controls="gr-snap-confirm" onClick={() => setConfirm((v) => !v)}>Submit anyway</Button> : null}
        </> : cycle ? <JumpLink to="gr-conmon-h" className="gp-linkbtn btn-primary">Prepare Snapshot</JumpLink> : null}
      </div>
      {confirm ? (
        <div className="gr-recform" id="gr-snap-confirm" data-part="snapshot-confirm">
          <span className="gr-small">{`Submit ${pct(score)}? GovRAMP flags a score equal to the last one.`}</span>
          <span className="gr-actions">
            <Button compact aria-disabled={busy || undefined} onClick={() => void submit()}>{busy ? 'Submitting…' : `Submit ${pct(score)}`}</Button>
            <Button compact variant="ghost" onClick={() => setConfirm(false)}>Keep working</Button>
          </span>
        </div>
      ) : null}
      <details className="gr-why"><summary>How the Snapshot score works</summary><div>
        <span>Sum of MITRE weights for fully met Snapshot controls, divided by the total weight.</span>
        <span>GovRAMP flags a score equal to or lower than the last one.</span>
      </div></details>
    </section>
  );
}

function Movers({ o, slug, prefix, role, canManage }: { o: GovrampOverview; slug: string; prefix: string; role: string; canManage: boolean }) {
  const movers = snapshotMovers(o).slice(0, 5);
  if (!movers.length) return null;
  const canUpload = canManage || ['owner', 'admin', 'editor'].includes(role);
  return (
    <section className="gr-card" aria-labelledby="gr-movers-h" data-part="movers">
      <div className="gr-card-head"><h2 id="gr-movers-h" className="gr-card-h" tabIndex={-1}>Raises your score most</h2></div>
      <ul className="gr-rows">
        {movers.map((m) => (
          <li key={m.requirementId} className="gr-row" data-part="mover">
            <span className="gr-row-main">
              <span className="gr-row-t"><span className="gr-ctl">{m.ref}</span>{m.title}</span>
              <span className="gr-row-meta"><Glyph status={m.status === 'not_met' ? 'not' : 'noev'} /><span>{m.status === 'not_met' ? 'Not met' : 'No evidence'}</span><span className="gr-num">{`+${pct(m.gainPct)}`}</span></span>
            </span>
            {m.status === 'not_met'
              ? <Link className="gr-go" to={`${prefix}/findings?requirement=${encodeURIComponent(m.ref)}`} aria-label={`Fix ${m.ref}`}>Fix ›</Link>
              : canUpload && m.control?.open_objective_ids.length
                ? <UploadForObjective slug={slug} program={o.profile_id} periodId={o.period!.period_id} refLabel={m.ref} label="Collect"
                    objectives={m.control.open_objective_ids.map((id) => ({ id, ref: id.split(':').pop()! }))} />
                : <Link className="gr-go" to={`${prefix}/evidence`}>Collect ›</Link>}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---- readiness hero ------------------------------------------------------------------------------

function ReadinessHero({ o, r, slug, canManage, pursuingReady, onToast, slim = false }: {
  o: GovrampOverview; r: NonNullable<ReturnType<typeof readinessModel>>; slug: string; canManage: boolean; pursuingReady: boolean;
  onToast: ReturnType<typeof useToast>[1];
  /** V109-5-5: the roll-up above already shows readiness and holds the page's primary — keep only the booking and the fixes. */
  slim?: boolean;
}) {
  const [sp] = useSearchParams();
  const [booking, setBooking] = useState(sp.get('book') === '1');
  const todayDay = dayOf(o);
  const tier = tierLabel(o.tier);
  const not = r.notMet.length, noev = r.noEvidence.length, cc = r.couldnt.length;
  return (
    <section className="gr-card" aria-labelledby="gr-rd-h" data-part="readiness">
      <h2 id="gr-rd-h" className="gr-hero-label">{slim ? `${tier} assessment` : `Readiness for ${tier}`}</h2>
      {slim ? null : <>
      <p className="gr-hero-fig"><b className="gr-num">{`${r.met} of ${r.total}`}</b><span className="gr-dim">controls met</span></p>
      <div className="gr-meter" role="img" aria-label={`${r.met} met, ${not} not met, ${cc ? `${cc} couldn't be checked, ` : ''}${noev} no evidence, of ${r.total}`}>
        {r.met ? <span className="gr-seg gr-seg--met" style={{ flexGrow: r.met }} /> : null}
        {not ? <span className="gr-seg gr-seg--not" style={{ flexGrow: not }} /> : null}
        {cc ? <span className="gr-seg gr-seg--cc" style={{ flexGrow: cc }} /> : null}
        {noev ? <span className="gr-seg gr-seg--noev" style={{ flexGrow: noev }} /> : null}
      </div>
      <p className="gr-legend" data-part="legend">
        <span><i className="gr-seg--met" />{`${r.met} met`}</span>
        <span><i className="gr-seg--not" />{not ? <JumpLink to="gr-notmet-h">{`${not} not met`}</JumpLink> : '0 not met'}</span>
        {cc ? <span><i className="gr-seg--cc" />{`${cc} couldn't check`}</span> : null}
        <span><i className="gr-seg--noev" />{noev ? <JumpLink to="gr-noev-h">{`${noev} no evidence`}</JumpLink> : '0 no evidence'}</span>
      </p>
      {/* V109-1-19 (B4) / R3 (Medium): the one hero count component — its way in lands on the list's first fix. */}
      <CouldntCheckCount count={cc} noun={['control', 'controls']} onClick={() => window.setTimeout(() => {
        const el = document.querySelector<HTMLElement>('[data-part="couldnt-controls"] [data-part="couldnt-check-fix"]') ?? document.getElementById('gr-cc-h');
        el?.scrollIntoView?.({ block: 'center' });
        el?.focus();
      }, 0)} />
      </>}
      {pursuingReady && o.ready_booking && !booking ? (
        <p className="gr-recout" data-part="ready-booked"><Glyph status="act" />{`Ready assessment booked · ${o.ready_booking.firm} · ${fmtShort(o.ready_booking.assessment_date, todayDay)}`}
          {canManage ? <button type="button" className="gr-mutedlink" onClick={() => setBooking(true)}>Change booking</button> : null}</p>
      ) : null}
      <div className="gr-actions">
        {pursuingReady && !o.ready_booking && !booking ? (canManage
          ? <><Button variant={slim ? 'secondary' : 'primary'} id="gr-book" aria-expanded={false} aria-controls="gr-book-form" onClick={() => setBooking(true)}>Record Ready booking</Button>
              <ExtLink href={FIND_3PAO_URL} label="Find a 3PAO on the GovRAMP site">Find a 3PAO</ExtLink></>
          : <AskAdmin slug={slug} compact what="runs the GovRAMP program" verb="record the Ready booking" subject="GAC Pro: record our GovRAMP Ready assessment booking" />) : null}
        {/* Built-UI #2: under the roll-up its legend opens these lists; no duplicate "Fix N not met" here. */}
        {not && !slim ? <JumpLink to="gr-notmet-h" className="gp-linkbtn">{`Fix ${not} not met`}</JumpLink> : null}
        {noev && !slim ? <JumpLink to="gr-noev-h" className="gp-linkbtn">{`Collect ${noev}`}</JumpLink> : null}
      </div>
      {booking ? (
        <div id="gr-book-form">
          <BookingForm slug={slug} program={o.profile_id} kind="ready_booking" idPrefix="gr-book" initial={o.ready_booking ? { firm: o.ready_booking.firm, date: o.ready_booking.assessment_date } : null}
            onCancel={() => setBooking(false)} onDone={(m) => { setBooking(false); onToast({ text: m, focusId: 'gr-rd-h' }); }} />
        </div>
      ) : null}
    </section>
  );
}

// ---- ConMon --------------------------------------------------------------------------------------

function ConmonCard({ c, o, s, slug, program, canManage, late, onToast }: {
  c: CycleModel; o: GovrampOverview; s: GovrampStatus; slug: string; program: string; canManage: boolean; late: boolean; onToast: ReturnType<typeof useToast>[1];
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lapseOpen, setLapseOpen] = useState(false);
  const todayDay = dayOf(o);
  const refresh = () => Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
  const run = async (key: string, fn: () => Promise<unknown>, msg: string, undo?: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(key); setError(null);
    try {
      await fn(); await refresh();
      onToast({ text: msg, focusId: 'gr-conmon-h', undo: undo ? async () => { await undo(); await refresh(); } : undefined });
    } catch (e) { setError(e instanceof Error ? e.message : 'That did not save.'); }
    finally { setBusy(null); }
  };
  const ready = c.left === 0;
  const lapsed = s.lapsed;
  const heading = late && lapsed ? `Submit ${c.label} · then contact GovRAMP` : c.label;
  const submitId = `gr-sub-${c.id}`;
  return (
    <section className="gr-card" data-tone={late ? 'bad' : undefined} aria-labelledby="gr-conmon-h" data-part={late ? 'conmon-late' : 'conmon'}>
      <div className="gr-card-head">
        <h2 id="gr-conmon-h" className="gr-card-h" tabIndex={-1}>{heading}</h2>
        {late ? <span className="gp-chip gp-chip--red">{`✕ Late · ${daysText(c.daysLeft)}`}</span>
          : <span className="gp-chip">{`${c.done === 0 ? 'Not started' : 'In progress'} · ${c.done} of ${c.total}`}</span>}
      </div>
      <p className="gr-small gr-dim">{`${c.cadence} · ${late ? 'was due' : 'due'} `}<b className="gr-strong">{fmtShort(c.due, todayDay)}</b>{late ? '' : ` · ${daysText(c.daysLeft)}`}{` · ${c.done} of ${c.total} done`}</p>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <ul className="gr-cm-list">
        {c.items.map((i) => (
          <li key={i.key} className="gr-cm-row" data-part="conmon-item">
            <span><Glyph status={i.done ? 'done' : late ? 'not' : 'ns'} /><span className="gr-cm-lbl">{i.label}</span>{i.required ? null : <span className="gr-small gr-dim">optional</span>}</span>
            <span>
              {i.done ? <>
                <span className="gr-small gr-dim">{`Done ${fmtShort(i.done.at, todayDay)}`}</span>
                <Link className="gr-mutedlink" to={`/w/${slug}/app${i.action.href}`} aria-label={`View ${i.label}`}>View</Link>
                {canManage ? <button type="button" className="gr-mutedlink" aria-label={`Mark ${i.label} not done`} onClick={() => void run(`u-${i.key}`, () => endProgramEvent(slug, program, i.done!.eventId, 'reopened'), `${i.label} reopened`)}>Reopen</button> : null}
              </> : <>
                <span className={`gr-small ${late ? 'gr-bad' : 'gr-dim'}`}>{late ? 'Overdue' : 'Not done'}</span>
                <Link className="gp-linkbtn gp-linkbtn--compact" to={`/w/${slug}/app${i.action.href}`} aria-label={`${i.action.label} · ${i.label}`}>{i.action.label}</Link>
                {canManage ? <Button compact variant="ghost" aria-disabled={busy === i.key || undefined} aria-label={`Mark ${i.label} done`}
                  onClick={() => void run(i.key, () => recordProgramEvent(slug, program, { kind: 'conmon_item_done', data: { cycle_id: c.id, item: i.key } }), `${i.label} done`)}>Mark done</Button> : null}
              </>}
            </span>
          </li>
        ))}
      </ul>
      <div className="gr-actions">
        {canManage ? <>
          <Button compact variant={ready ? 'primary' : 'secondary'} aria-disabled={!ready || busy === 'submit' || undefined} aria-describedby={ready ? undefined : submitId}
            onClick={() => { if (ready) void run('submit', () => submitConmonCycle(slug, program, c.periodId, c.id, c.kind === 'snapshot' && o.snapshot ? { snapshot_score_percent: o.snapshot.score.percent } : {}), `${c.label} submitted`); }}>{`Submit ${c.label}`}</Button>
          {ready ? null : <span id={submitId} className="gr-small gr-dim">{`Opens at ${c.total} of ${c.total}`}</span>}
        </> : <AskAdmin slug={slug} compact what="runs the GovRAMP program" verb={`submit ${c.label}`} subject={`GAC Pro: ${c.label} for GovRAMP`} />}
        {late && canManage && !lapsed ? (
          <button type="button" className="gr-mutedlink" aria-expanded={lapseOpen} aria-controls="gr-lapse-form" onClick={() => setLapseOpen((v) => !v)} data-part="lapse-open">Record a lapse notice</button>
        ) : null}
        {late && canManage && lapsed && o.lapse ? (
          <button type="button" className="gr-mutedlink" data-part="lapse-remove"
            onClick={() => { const l = o.lapse!; void run('rm-lapse', () => endProgramEvent(slug, program, l.event_id, 'removed'), 'Lapse notice removed',
              () => recordProgramEvent(slug, program, { kind: 'govramp_lapse_notice', data: { notice_date: l.notice_date, ...(l.note ? { note: l.note } : {}) } })); }}>Remove lapse notice</button>
        ) : null}
      </div>
      {lapseOpen ? (
        <div id="gr-lapse-form">
          <LapseNoticeForm slug={slug} program={program} onCancel={() => setLapseOpen(false)} onDone={(m) => { setLapseOpen(false); onToast({ text: m, focusId: 'gr-conmon-h' }); }} />
        </div>
      ) : null}
    </section>
  );
}

function NoCycles({ o, slug, program, canManage }: { o: GovrampOverview; slug: string; program: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    if (busy || !o.period) return;
    setBusy(true); setError(null);
    try {
      await generateConmonCycles(slug, program, o.period.period_id);
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
    } catch (e) { setError(e instanceof Error ? e.message : 'The cycles could not be set up.'); }
    finally { setBusy(false); }
  };
  return (
    <section className="gr-card" aria-labelledby="gr-conmon-h" data-part="conmon-none">
      <h2 id="gr-conmon-h" className="gr-card-h" tabIndex={-1}>{o.tier === 'snapshot' ? 'Snapshot cycles' : 'ConMon'}</h2>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <EmptyState title="No open cycle" action={canManage
        ? <Button aria-disabled={busy || undefined} onClick={() => void go()}>{busy ? 'Setting up…' : 'Set up ConMon cycles'}</Button>
        : <AskAdmin slug={slug} compact what="runs the GovRAMP program" verb="set up ConMon cycles" subject="GAC Pro: set up GovRAMP ConMon cycles" />} />
    </section>
  );
}

// ---- open controls -------------------------------------------------------------------------------

function ControlList({ rows, kind, slug, prefix, program, periodId, canUpload, fedrampTests }: {
  rows: GovrampControl[]; kind: 'not_met' | 'no_evidence'; slug: string; prefix: string; program: string; periodId: string; canUpload: boolean; fedrampTests: ReadonlySet<string>;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, 3);
  const id = kind === 'not_met' ? 'gr-notmet-h' : 'gr-noev-h';
  const listId = `${id}-list`;
  const shared = (r: GovrampControl) => r.test_keys.some((k) => fedrampTests.has(k));
  return (
    <div className="gr-stack">
      <div className="gr-card-head">
        <h3 id={id} className="gr-sub-h" tabIndex={-1}>{`${kind === 'not_met' ? 'Not met' : 'No evidence'} · ${rows.length}`}</h3>
        {rows.length > 3 ? <button type="button" className="gr-mutedlink" aria-expanded={all} aria-controls={listId} onClick={() => setAll((v) => !v)}>{all ? 'Show fewer' : `All ${rows.length}`}</button> : null}
      </div>
      {rows.length ? (
        <ul className="gr-rows" id={listId}>
          {shown.map((r) => (
            <li key={r.requirement_id}>
              {kind === 'not_met' ? (
                <Link className="gr-row" to={`${prefix}/findings?requirement=${encodeURIComponent(r.ref)}`} data-part="control-fix">
                  <span className="gr-row-main">
                    <span className="gr-row-t"><span className="gr-ctl">{r.ref}</span>{r.title}</span>
                    <span className="gr-row-meta"><Glyph status="not" /><span>Not met</span>{shared(r) ? <span className="gr-htag">Also counts for FedRAMP 20x</span> : null}</span>
                  </span>
                  <span className="gr-go">Fix ›</span>
                </Link>
              ) : (
                <div className="gr-row" data-part="control-collect">
                  <span className="gr-row-main">
                    <span className="gr-row-t"><span className="gr-ctl">{r.ref}</span>{r.title}</span>
                    <span className="gr-row-meta"><Glyph status="noev" /><span>No evidence</span>{shared(r) ? <span className="gr-htag">Also counts for FedRAMP 20x</span> : null}</span>
                  </span>
                  {canUpload && r.open_objective_ids.length
                    ? <UploadForObjective slug={slug} program={program} periodId={periodId} refLabel={r.ref} label="Upload"
                        objectives={r.open_objective_ids.map((oid) => ({ id: oid, ref: oid.split(':').pop()! }))} />
                    : <Link className="gr-go" to={`${prefix}/evidence`}>Collect ›</Link>}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : <p className="gr-small gr-dim">{kind === 'not_met' ? 'Nothing not met.' : 'Every control has evidence.'}</p>}
    </div>
  );
}

function OpenControls({ r, slug, prefix, program, periodId, role, canManage, fedrampTests }: {
  r: NonNullable<ReturnType<typeof readinessModel>>; slug: string; prefix: string; program: string; periodId: string; role: string; canManage: boolean; fedrampTests: ReadonlySet<string>;
}) {
  const open = r.notMet.length + r.noEvidence.length + r.couldnt.length;
  if (!open) return null;
  const canUpload = canManage || ['owner', 'admin', 'editor'].includes(role);
  return (
    <section className="gr-card" aria-labelledby="gr-open-h" data-part="open-controls">
      <h2 id="gr-open-h" className="gr-card-h">{`Open controls · ${open}`}</h2>
      {r.couldnt.length ? <CouldntList rows={r.couldnt} slug={slug} prefix={prefix} program={program} periodId={periodId} canManage={canManage} /> : null}
      <div className="gr-grid2">
        <ControlList rows={r.notMet} kind="not_met" slug={slug} prefix={prefix} program={program} periodId={periodId} canUpload={canUpload} fedrampTests={fedrampTests} />
        <ControlList rows={r.noEvidence} kind="no_evidence" slug={slug} prefix={prefix} program={program} periodId={periodId} canUpload={canUpload} fedrampTests={fedrampTests} />
      </div>
    </section>
  );
}

/**
 * V109-1-19 (B4): controls GAC Pro couldn't check — why (each reason, the unverified resources) and
 * the fix per source (the Setup step for that source); the control opens its own page.
 */
function CouldntList({ rows, slug, prefix, program, periodId, canManage }: { rows: GovrampControl[]; slug: string; prefix: string; program: string; periodId: string; canManage: boolean }) {
  const viewerId = useOptionalAuth()?.user?.id ?? '';
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ['ws-members', slug], queryFn: () => listWorkspaceMembers(slug), staleTime: 60_000, retry: false });
  const ownerName = (id: string) => members.data?.members.find((m) => m.user_id === id)?.display_name ?? null;
  const itemsOf = (r: GovrampControl) => objectiveItems(r.couldnt_check ? { reasons: r.couldnt_check.reasons, unverified: 0, resources: 0, by_source: r.couldnt_check.by_source } : null);
  // V109-1-19 R3 (Medium): accept the risk once for the list (owner / admin), for controls no open acceptance covers.
  const targets = rows.filter((r) => !coveringAcceptance(r.risk_acceptances)).map((r) => {
    const kinds = [...new Set(itemsOf(r).map((i) => i.kind).filter((k): k is string => !!k))];
    return { label: `${r.ref} ${r.title}`, registerItemId: r.requirement_id, connection: kinds.length === 1 ? sourceNameOf(kinds[0]!) : null };
  });
  return (
    <div className="gr-stack" data-part="couldnt-controls">
      <h3 id="gr-cc-h" className="gr-sub-h" tabIndex={-1}>{`Couldn't check · ${rows.length}`}</h3>
      {canManage ? <AcceptRisk slug={slug} viewerId={viewerId} targets={targets} onDone={() => void qc.invalidateQueries({ queryKey: ['govramp-overview', slug] })} /> : null}
      <ul className="gr-rows">
        {rows.map((r) => <GovrampCouldntRow key={r.requirement_id} r={r} items={itemsOf(r)} prefix={prefix} program={program} periodId={periodId} ownerName={ownerName} />)}
      </ul>
    </div>
  );
}

/** One control GAC Pro couldn't check: why, the fix per source (where the fix happens), an acceptance that holds, one toggle. */
function GovrampCouldntRow({ r, items, prefix, program, periodId, ownerName }: {
  r: GovrampControl; items: ReturnType<typeof objectiveItems>; prefix: string; program: string; periodId: string; ownerName: (id: string) => string | null;
}) {
  const [all, setAll] = useState(false);
  const accepted = coveringAcceptance(r.risk_acceptances);
  const to = `${prefix}/programs/${program}/controls/${encodeURIComponent(r.requirement_id)}`;
  return (
    <li data-check-state="couldnt_check">
      <div className="gr-row">
        <span className="gr-row-main">
          {/* R3 (Low): a space between the ref and the title inside the link, so its name reads "AC-2 Account management". */}
          <Link className="gr-row-t" to={to}><span className="gr-ctl">{r.ref}</span>{' '}{r.title}</Link>
          <span className="gr-row-meta"><CouldntCheckChip /><CouldntCheckWhy items={items} unit="resource" expanded={all} /></span>
          {accepted ? <AcceptedLine acceptance={accepted} ownerName={ownerName(accepted.owner_user_id)} /> : null}
          <ShowAllReasons items={items} expanded={all} onToggle={() => setAll((x) => !x)} />
        </span>
        <CouldntCheckFixes items={items} prefix={prefix} ctx={{ program, requirement_id: r.requirement_id, period_id: periodId, ref: r.ref, returnTo: `${prefix}/programs/${program}` }} compact expanded={all} />
      </div>
    </li>
  );
}

// ---- tier change ---------------------------------------------------------------------------------

function TierChange({ o, s, slug, program, to, hasFedramp, onClose }: {
  o: GovrampOverview; s: GovrampStatus; slug: string; program: string; to: string; hasFedramp: boolean; onClose: (msg?: string) => void;
}) {
  const qc = useQueryClient();
  const delta = useQuery({ queryKey: queryKeys.govrampTierDelta(slug, program, to), queryFn: () => getGovrampTierDelta(slug, program, to), retry: false, staleTime: 5 * 60_000 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, []);
  const r = readinessModel(o);
  const from = tierLabel(o.tier), target = tierLabel(to);
  const blocked = s.tierChangeBlocked;
  const go = async () => {
    if (busy || blocked) return;
    setBusy(true); setError(null);
    try {
      await changeGovrampTier(slug, program, { to_tier: to });
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
      onClose(`Pursuing ${target} · ${tierLabel(s.held ?? o.tier)} stays held`);
    } catch (e) { setError(e instanceof Error ? e.message : 'The tier change did not save.'); }
    finally { setBusy(false); }
  };
  const d = delta.data;
  const fromCount = d ? d.shared + d.removed.length : r?.total ?? 0;
  const toCount = d ? d.shared + d.added.length : null;
  return (
    <section className="gr-dialog" aria-labelledby="gr-tc-h" data-part="tier-change">
      <div className="gr-card-head">
        <h2 id="gr-tc-h" ref={heading} tabIndex={-1} className="gr-card-h">{`Start pursuing ${target}`}</h2>
        <span className="gr-small gr-dim">{`${tierLabel(o.impact_level)} · ${fromCount}${toCount != null ? ` → ${toCount}` : ''} controls · ${tierLabel(s.held ?? o.tier)} stays held until ${target} is granted`}</span>
      </div>
      {blocked ? (
        <Alert variant="error"><p><b>{blocked === 'lapsed' ? `${tierLabel(s.held ?? o.tier)} lapsed` : `${s.cycle?.label ?? 'ConMon'} is ${daysText(s.cycle?.daysLeft ?? 0)} late`}</b>
          {` · you can start ${target} once ${blocked === 'lapsed' ? `${tierLabel(s.held ?? o.tier)} is restored` : 'it’s submitted'}`}</p></Alert>
      ) : null}
      {error ? <Alert variant="error">{error}</Alert> : null}
      {delta.isLoading ? <Spinner label="Loading the tier change" /> : delta.error || !d ? <Alert variant="error"><p>The tier change could not load.</p><Button onClick={() => void delta.refetch()}>Retry</Button></Alert> : (
        <div className="gr-compare">
          <div className="gr-box">
            <h4>Carries over</h4>
            <p className="gr-big">{d.shared} <span className="gr-small gr-dim">controls</span></p>
            {/* Owner decision 2026-10-18: the same carry rule as SOC 2 / FedRAMP — your own and automated results carry;
                3PAO results stay in the closed period and those objectives need assessing again in the new tier. */}
            {d.carry ? (
              <p className="gr-small" data-part="tier-carry">{[
                `${d.carry.carried} reviewed ${d.carry.carried === 1 ? 'result carries' : 'results carry'} over`,
                d.carry.left_assessor ? `${d.carry.left_assessor} assessor ${d.carry.left_assessor === 1 ? 'result stays' : 'results stay'} in the closed period and need assessing again` : null,
              ].filter(Boolean).join(' · ')}</p>
            ) : null}
            {r ? (
              <ul className="gr-flat">
                {d.carry ? null : <li><span><b className="gr-num">{r.met}</b> met · reviewed results kept</span><span className="gp-chip gp-chip--ok">✓ Kept</span></li>}
                <li><span><b className="gr-num">{r.notMet.length}</b> not met · stay open</span>{r.notMet.length ? <JumpLink to="gr-notmet-h" className="gr-textlink">{`Fix ${r.notMet.length}`}</JumpLink> : null}</li>
                <li><span><b className="gr-num">{r.noEvidence.length}</b> no evidence · stay open</span>{r.noEvidence.length ? <JumpLink to="gr-noev-h" className="gr-textlink">{`Collect ${r.noEvidence.length}`}</JumpLink> : null}</li>
              </ul>
            ) : null}
          </div>
          <div className="gr-box">
            <h4>{`New for ${target}`}</h4>
            <p className="gr-big">{d.added.length} <span className="gr-small gr-dim">controls</span></p>
            <p className="gr-small gr-dim">Start with no evidence</p>
            {d.added.length ? (
              <details className="gr-why"><summary>{`Show ${d.added.length} new controls`}</summary><div>
                <ul className="gr-idl">{d.added.map((a) => <li key={a.requirement_id}><span className="gr-ctl">{a.ref}</span>{a.title}</li>)}</ul>
              </div></details>
            ) : null}
          </div>
        </div>
      )}
      <ul className="gr-flat">
        {s.cycle ? <li><span>{s.cycle.label}</span><span className={s.cycle.late ? 'gr-bad' : undefined}>{`${s.cycle.late ? 'Was due' : 'Still due'} ${fmtShort(s.cycle.due, dayOf(o))} · ${s.cycle.left} left`}</span></li> : null}
        {o.tier === 'core' && to === 'ready' ? <li><span>ConMon</span><span>{'Quarterly while Core is held '}<span aria-hidden="true">→</span><span className="gp-sr-only">, becomes</span>{' '}<b>monthly</b>{' once Ready is granted'}</span></li> : null}
        <li><span>Assessment</span><span className="gr-actions">{`${target} assessment by a 3PAO`}<ExtLink href={FIND_3PAO_URL} label="Find a 3PAO on the GovRAMP site">Find a 3PAO</ExtLink></span></li>
        {hasFedramp && to === 'ready' ? <li><span>FedRAMP 20x Class A</span><span>RAR + SAP from your Ready assessment · usable 12 months after it</span></li> : null}
      </ul>
      {d ? <details className="gr-why"><summary>{`Why ${d.added.length} more controls`}</summary><div><span>{`GovRAMP ${target} is ${toCount} controls; ${from} is ${fromCount}.`}</span></div></details> : null}
      <div className="gr-actions">
        {blocked ? <>
          {blocked === 'conmon_late' ? <JumpLink to="gr-conmon-h" className="gp-linkbtn btn-primary">{`Finish ${s.cycle?.label ?? 'ConMon'}`}</JumpLink> : null}
          <Button aria-disabled="true" aria-describedby="gr-tc-why">{`Start pursuing ${target}`}</Button>
          <span id="gr-tc-why" className="gr-small gr-dim">{blocked === 'lapsed' ? `After ${tierLabel(s.held ?? o.tier)} is restored` : `After ${s.cycle?.label ?? 'ConMon'} is submitted`}</span>
          <Button variant="ghost" onClick={() => onClose()}>Close</Button>
        </> : <>
          <Button variant="primary" aria-disabled={busy || !d || undefined} onClick={() => void go()}>{busy ? 'Starting…' : `Start pursuing ${target}`}</Button>
          <Button variant="ghost" onClick={() => onClose()}>{`Stay on ${from}`}</Button>
        </>}
      </div>
    </section>
  );
}

// ---- Audit: GovRAMP's decision + the Ready booking ------------------------------------------------

export function GovrampAudit({ card, slug, canManage, fallback }: Pick<GovrampPanelProps, 'card' | 'slug' | 'canManage' | 'fallback'>) {
  const program = card.profile_id;
  const q = useGovrampOverview(slug, program);
  const qc = useQueryClient();
  const [toastNode, toast] = useToast();
  const [grantOpen, setGrantOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [tier, setTier] = useState('');
  const [date, setDate] = useState('');
  const [errs, setErrs] = useState<{ tier?: string; date?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (q.isLoading) return <Spinner label="Loading GovRAMP" />;
  if (q.error || !q.data) return <Alert variant="error"><p>GovRAMP could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  const o = q.data;
  const todayDay = dayOf(o);
  const s = govrampStatus(o, todayDay);
  const pursuingReady = !!s.pursuing && ['ready', 'authorized', 'provisional'].includes(s.pursuing);
  const record = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const t = tier || o.tier || '';
    const next: typeof errs = {};
    if (!t) next.tier = 'Choose the tier GovRAMP granted.';
    if (!date) next.date = 'Choose the date of GovRAMP’s decision.';
    else if (date > todayDay) next.date = 'The decision date can’t be in the future.';
    setErrs(next);
    if (Object.keys(next).length) return;
    setBusy(true); setError(null);
    try {
      await recordProgramEvent(slug, program, { kind: 'govramp_status_granted', data: { tier: t, granted_on: date } });
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
      setGrantOpen(false);
      toast({ text: `${tierLabel(t)} granted · recorded`, focusId: 'gr-audit-h' });
    } catch (ex) { setError(ex instanceof Error ? ex.message : 'The decision could not be recorded.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="gr-page" data-part="govramp-audit">
      {toastNode}
      <section className="gr-card" aria-labelledby="gr-audit-h">
        <h3 id="gr-audit-h" className="gr-card-h" tabIndex={-1}>GovRAMP decision</h3>
        <p data-part="held">{o.held ? `${tierLabel(o.held.tier)} granted ${fmtShort(o.held.granted_on, todayDay)}` : `No GovRAMP decision recorded · pursuing ${tierLabel(o.tier)}`}</p>
        {canManage ? (grantOpen ? (
          <form className="gr-recform" onSubmit={record} noValidate aria-label="Record GovRAMP’s decision">
            {error ? <Alert variant="error">{error}</Alert> : null}
            <Field id="gr-grant-tier" label="Tier granted" required error={errs.tier}>
              <Select value={tier || o.tier || ''} onChange={(e) => setTier(e.currentTarget.value)}>
                {['snapshot', 'core', 'ready', 'authorized', 'provisional'].map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}
              </Select>
            </Field>
            <Field id="gr-grant-date" label="Decision date" required error={errs.date}>
              <Input type="date" value={date} max={todayDay} onChange={(e) => setDate(e.currentTarget.value)} />
            </Field>
            <span className="gr-actions">
              <Button type="submit" variant="primary" compact aria-disabled={busy || undefined}>{busy ? 'Recording…' : 'Record'}</Button>
              <Button variant="ghost" compact onClick={() => setGrantOpen(false)}>Cancel</Button>
            </span>
          </form>
        ) : <div className="gr-actions"><Button onClick={() => setGrantOpen(true)}>{o.held ? 'Record a new decision' : `Record ${tierLabel(o.tier)} granted`}</Button></div>)
          : <AskAdmin slug={slug} compact what="runs the GovRAMP program" verb="record GovRAMP’s decision" subject="GAC Pro: record our GovRAMP decision" />}
      </section>
      {pursuingReady ? (
        <section className="gr-card" aria-labelledby="gr-audit-book-h">
          <h3 id="gr-audit-book-h" className="gr-card-h" tabIndex={-1}>Ready assessment</h3>
          <p data-part="booking">{o.ready_booking ? `Booked · ${o.ready_booking.firm} · ${fmtShort(o.ready_booking.assessment_date, todayDay)}` : 'Not booked'}</p>
          {canManage ? (bookOpen
            ? <BookingForm slug={slug} program={program} kind="ready_booking" idPrefix="gr-audit-book" initial={o.ready_booking ? { firm: o.ready_booking.firm, date: o.ready_booking.assessment_date } : null}
                onCancel={() => setBookOpen(false)} onDone={(m) => { setBookOpen(false); toast({ text: m, focusId: 'gr-audit-book-h' }); }} />
            : <div className="gr-actions"><Button variant={o.ready_booking ? 'secondary' : 'primary'} onClick={() => setBookOpen(true)}>{o.ready_booking ? 'Change booking' : 'Record Ready booking'}</Button>
                <ExtLink href={FIND_3PAO_URL} label="Find a 3PAO on the GovRAMP site">Find a 3PAO</ExtLink></div>)
            : <AskAdmin slug={slug} compact what="runs the GovRAMP program" verb="record the Ready booking" subject="GAC Pro: record our GovRAMP Ready assessment booking" />}
        </section>
      ) : null}
      {fallback}
    </div>
  );
}
